import { useState, useEffect } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL
const DIAS_CORTO = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab']

function formatoFecha(iso) {
  if (!iso) return ''
  const [y, m, d] = iso.split('-').map(Number)
  const fecha = new Date(y, m - 1, d)
  return `${DIAS_CORTO[fecha.getDay()]} ${d}/${m}`
}

function formatoHora(horaSql) {
  if (!horaSql) return ''
  const [h, m] = horaSql.split(':').map(Number)
  const ampm = h >= 12 ? 'PM' : 'AM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`
}

// Circulo con la foto real del profesor si existe, o su emoji de respaldo.
// Es un boton -- al hacer click abre el perfil completo (ver ModalPerfilProfesor).
function AvatarProfesor({ profesor, tam = 40, fontSize = 20, onClick }) {
  return (
    <button onClick={onClick} disabled={!profesor} title={profesor ? 'Ver perfil del profesor' : ''}
      style={{ width: tam, height: tam, borderRadius: '50%', background: '#E8EFFE', border: 'none', padding: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize, flexShrink: 0, cursor: profesor ? 'pointer' : 'default' }}>
      {profesor?.foto_url
        ? <img src={profesor.foto_url} alt={profesor.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : (profesor?.avatar_emoji || '🧑‍🏫')}
    </button>
  )
}

// Perfil completo del profesor -- se abre al hacer click en su foto/nombre.
function ModalPerfilProfesor({ profesor, onCerrar }) {
  if (!profesor) return null
  return (
    <div onClick={onCerrar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 380, width: '100%', textAlign: 'center' }}>
        <div style={{ width: 96, height: 96, borderRadius: '50%', background: '#E8EFFE', margin: '0 auto 16px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 44 }}>
          {profesor.foto_url
            ? <img src={profesor.foto_url} alt={profesor.nombre} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : (profesor.avatar_emoji || '🧑‍🏫')}
        </div>
        <div style={{ fontSize: 18, fontWeight: 800, color: '#1A1A2E', marginBottom: 4 }}>{profesor.nombre}</div>
        {profesor.niveles?.length > 0 && (
          <div style={{ fontSize: 12, color: '#1B3A8C', fontWeight: 700, marginBottom: 12 }}>Enseña: {profesor.niveles.join(', ')}</div>
        )}
        {profesor.bio && (
          <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, textAlign: 'left' }}>{profesor.bio}</p>
        )}
        <button onClick={onCerrar} style={{ marginTop: 16, padding: '9px 24px', borderRadius: 10, border: '1.5px solid #E2E6F0', background: '#fff', color: '#374151', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          Cerrar
        </button>
      </div>
    </div>
  )
}

export default function ClasesEnVivo({ usuario, onUnirse }) {
  const [tipo, setTipo] = useState('grupal') // 'grupal' | 'privada'
  const [niveles, setNiveles] = useState([])
  const [nivelActivo, setNivelActivo] = useState(null)
  const [clases, setClases] = useState([])
  const [misReservas, setMisReservas] = useState([])
  const [cargando, setCargando] = useState(true)
  const [reservando, setReservando] = useState(null) // id de la clase mientras se procesa
  const [mensaje, setMensaje] = useState(null) // { tipo: 'ok' | 'error', texto }
  const [perfilProfesor, setPerfilProfesor] = useState(null) // profesor que se esta viendo en el modal

  useEffect(() => {
    axios.get(`${API}/curriculo/niveles`).then(r => {
      const datos = r.data.datos || []
      setNiveles(datos)
      if (datos.length > 0) setNivelActivo(datos[0].id)
    }).catch(() => {})
  }, [])

  function cargarMisReservas() {
    if (!usuario?.id) return
    axios.get(`${API}/clases/mis-reservas/${usuario.id}`)
      .then(r => setMisReservas(r.data.datos || []))
      .catch(() => {})
  }

  useEffect(() => { cargarMisReservas() }, [usuario])

  useEffect(() => {
    setCargando(true)
    const params = { tipo }
    if (tipo === 'grupal' && nivelActivo) params.nivel_id = nivelActivo
    axios.get(`${API}/clases`, { params })
      .then(r => setClases(r.data.datos || []))
      .catch(() => setClases([]))
      .finally(() => setCargando(false))
  }, [tipo, nivelActivo])

  async function reservar(clase) {
    if (!usuario?.id) {
      setMensaje({ tipo: 'error', texto: 'Necesitas iniciar sesion para reservar una clase.' })
      return
    }
    setReservando(clase.id)
    setMensaje(null)
    try {
      await axios.post(`${API}/clases/reservar`, { usuario_id: usuario.id, clase_id: clase.id })
      setMensaje({ tipo: 'ok', texto: `Clase reservada para ${formatoFecha(clase.fecha)} a las ${formatoHora(clase.hora_inicio)}.` })
      cargarMisReservas()
    } catch (err) {
      const texto = err.response?.data?.error || 'No se pudo reservar la clase. Intenta de nuevo.'
      setMensaje({ tipo: 'error', texto })
    } finally {
      setReservando(null)
    }
  }

  async function cancelar(reservaId) {
    if (!usuario?.id) return
    try {
      await axios.delete(`${API}/clases/reservas/${reservaId}`, { data: { usuario_id: usuario.id } })
      cargarMisReservas()
    } catch (err) {
      const texto = err.response?.data?.error || 'No se pudo cancelar la reserva.'
      setMensaje({ tipo: 'error', texto })
    }
  }

  function yaReservada(claseId) {
    return misReservas.some(r => r.clase_id === claseId)
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', animation: 'fadeIn .3s ease both' }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1A1A2E', marginBottom: 6 }}>🎥 Clases en vivo</h1>
      <p style={{ fontSize: 14, color: '#6B7280', marginBottom: 24 }}>
        Reserva clases grupales con estudiantes de tu mismo nivel, o clases privadas 1 a 1 con un profesor.
      </p>

      {mensaje && (
        <div style={{ background: mensaje.tipo === 'ok' ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${mensaje.tipo === 'ok' ? '#22C55E' : '#EF4444'}`, borderRadius: 10, padding: '10px 16px', marginBottom: 20, fontSize: 13, color: mensaje.tipo === 'ok' ? '#166534' : '#991B1B' }}>
          {mensaje.tipo === 'ok' ? '✅ ' : '⚠ '}{mensaje.texto}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button onClick={() => setTipo('grupal')}
          style={{ flex: 1, padding: '10px', borderRadius: 10, border: `2px solid ${tipo === 'grupal' ? '#1B3A8C' : '#E2E6F0'}`, background: tipo === 'grupal' ? '#1B3A8C' : '#fff', color: tipo === 'grupal' ? '#fff' : '#374151', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          👥 Clases grupales
        </button>
        <button onClick={() => setTipo('privada')}
          style={{ flex: 1, padding: '10px', borderRadius: 10, border: `2px solid ${tipo === 'privada' ? '#D4AF37' : '#E2E6F0'}`, background: tipo === 'privada' ? '#D4AF37' : '#fff', color: tipo === 'privada' ? '#0D1F52' : '#374151', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          🎓 Clases privadas
        </button>
      </div>

      {tipo === 'grupal' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 20, overflowX: 'auto', paddingBottom: 4 }}>
          {niveles.map(n => (
            <button key={n.id} onClick={() => setNivelActivo(n.id)}
              style={{ padding: '7px 16px', borderRadius: 999, border: `2px solid ${nivelActivo === n.id ? '#1B3A8C' : '#E2E6F0'}`, background: nivelActivo === n.id ? '#1B3A8C' : '#fff', color: nivelActivo === n.id ? '#fff' : '#374151', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {n.nombre}
            </button>
          ))}
        </div>
      )}

      {cargando ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Cargando clases...</div>
      ) : clases.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6B7280', background: '#fff', border: '1px solid #E2E6F0', borderRadius: 14, marginBottom: 32 }}>
          {tipo === 'grupal' ? 'Todavia no hay clases grupales programadas para este nivel.' : 'Todavia no hay clases privadas disponibles.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 32 }}>
          {clases.map(c => {
            const reservada = yaReservada(c.id)
            return (
              <div key={c.id} style={{ background: '#fff', border: '1px solid #E2E6F0', borderRadius: 14, padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <AvatarProfesor profesor={c.profesor} onClick={() => setPerfilProfesor(c.profesor)} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1A1A2E' }}>
                      {c.titulo || (c.tipo_clase === 'grupal' ? 'Clase grupal' : 'Clase privada')}
                    </div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>
                      {c.profesor?.nombre} · {formatoFecha(c.fecha)} · {formatoHora(c.hora_inicio)} · {c.duracion_min} min
                      {c.nivel_id && <span> · Nivel {c.nivel_id}</span>}
                    </div>
                    {c.requisitos && (
                      <div style={{ fontSize: 12, color: '#374151', marginTop: 6, background: '#F8FAFF', borderRadius: 8, padding: '6px 10px' }}>
                        📋 {c.requisitos}
                      </div>
                    )}
                  </div>
                  <button disabled={reservada || reservando === c.id} onClick={() => reservar(c)}
                    style={{ padding: '8px 16px', borderRadius: 10, border: `1.5px solid ${reservada ? '#22C55E' : '#E2E6F0'}`, background: reservada ? '#F0FDF4' : '#1B3A8C', color: reservada ? '#166534' : '#fff', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: reservada ? 'default' : 'pointer', flexShrink: 0 }}>
                    {reservada ? '✓ Reservada' : reservando === c.id ? 'Reservando...' : 'Reservar'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <h2 style={{ fontSize: 17, fontWeight: 700, marginBottom: 14 }}>📅 Mis próximas clases</h2>
      {misReservas.length === 0 ? (
        <p style={{ fontSize: 13, color: '#6B7280' }}>Todavia no tienes clases reservadas.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {misReservas.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 14, background: '#fff', border: '1px solid #E2E6F0', borderRadius: 12, padding: '12px 16px', flexWrap: 'wrap' }}>
              <AvatarProfesor profesor={r.clase?.profesor} tam={40} fontSize={22} onClick={() => setPerfilProfesor(r.clase?.profesor)} />
              <div style={{ flex: 1, minWidth: 160 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  {r.clase?.titulo || (r.tipo_clase === 'grupal' ? 'Grupal' : 'Privada')} · {r.clase?.profesor?.nombre}
                </div>
                <div style={{ fontSize: 12, color: '#6B7280' }}>{formatoFecha(r.fecha)} · {formatoHora(r.clase?.hora_inicio)}</div>
                {r.clase?.sala_estado === 'en_vivo' && (
                  <div style={{ fontSize: 11, color: '#166534', fontWeight: 700, marginTop: 2 }}>● El profesor ya inició la clase</div>
                )}
                {r.clase?.grabacion_url && (
                  <a href={r.clase.grabacion_url} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: '#1B3A8C', fontWeight: 700 }}>▶ Ver grabación</a>
                )}
              </div>
              {r.clase?.sala_estado === 'en_vivo' && (
                <button onClick={() => onUnirse(r.clase_id)}
                  style={{ background: '#1B3A8C', border: 'none', color: '#fff', borderRadius: 8, padding: '7px 14px', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  🎥 Unirse a la clase
                </button>
              )}
              <button onClick={() => cancelar(r.id)}
                style={{ background: 'none', border: '1px solid #EF4444', color: '#EF4444', borderRadius: 8, padding: '6px 12px', fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Cancelar
              </button>
            </div>
          ))}
        </div>
      )}

      <ModalPerfilProfesor profesor={perfilProfesor} onCerrar={() => setPerfilProfesor(null)} />
    </div>
  )
}
